import React from 'react';
import Editable from '../../components/Editable';
import { stripHtml, getFontStyles } from '../../utils/styleHelpers';
import { 
    getHeroTitleStyles, 
    getHeroSubtitleStyles, 
    normalizeHeroTitleProp, 
    normalizeHeroSubtitleProp 
} from '../../components/HeroBlock';
import { ECOMMERCE_BRANDING } from '../branding';

export const ShopHero = ({
    hero,
    shopHero,
    isAllView,
    isHeadingShow,
    isHeroBlockVisible = false,
    isEditing = false,
    currentCategoryObj,
    settings = {},
    typography = {},
    styleSettings = {},
    dbCategories = [],
    setDbCategories,
    setProducts,
    handleToggleHideCategory,
    handleEditCategory,
    update,
    _getFontStyles,
    activeCategory = 'All',
    activeSubCategory = 'All',
    directCatMatch,
    primaryColor,
    secondaryColor,
    effectiveSlugParam,
    navigate
}) => {
    const heroConfig = hero || shopHero || {};
    const isAllProductsView = isAllView !== undefined 
        ? isAllView 
        : (activeCategory === 'All' || !activeCategory);

    // If the rich Hero Block is visible on the page, the Hero Block is the primary header for "All Products".
    // Do NOT duplicate the Hero heading and subtitle in the section below the Hero.
    if (isHeroBlockVisible && isAllProductsView) {
        return null;
    }

    const isHeadingVisible = isHeadingShow !== undefined 
        ? isHeadingShow 
        : (isAllProductsView 
            ? (styleSettings.shopAllHeadingShow !== false && styleSettings.shopAllHeadingShow !== 'false')
            : (styleSettings.shopCategoryHeadingShow !== false && styleSettings.shopCategoryHeadingShow !== 'false'));

    if (!isHeadingVisible && !isEditing) return null;

    const resolveFontStyles = typeof _getFontStyles === 'function' 
        ? _getFontStyles 
        : getFontStyles(typography || settings?.typography || {});

    const isCurrentCatHidden = currentCategoryObj ? (
        currentCategoryObj.hidden === 1 || currentCategoryObj.hidden === true || currentCategoryObj.hidden === '1' || currentCategoryObj.hidden === 'true'
    ) : false;

    return (
        <div style={{ textAlign: 'center', marginBottom: styleSettings.shopCategoriesPlacement === 'above-heading' ? '30px' : '40px' }}>
            {!isHeadingVisible && isEditing && (
                <div style={{ display: 'inline-block', padding: '3px 8px', background: '#fef3c7', color: '#92400e', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 'bold', marginBottom: '8px', border: '1px dashed #f59e0b' }}>
                    👁️ Hidden on Live Store ({isAllProductsView ? '"ALL" Products View' : 'Category View'}) - Manage in Customizer
                </div>
            )}

            {isEditing && !isAllProductsView && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                    <button
                        type="button"
                        onClick={() => {
                            if (handleToggleHideCategory) {
                                handleToggleHideCategory(currentCategoryObj || activeCategory);
                            }
                        }}
                        style={{
                            background: isCurrentCatHidden ? '#fef2f2' : '#f0fdf4',
                            color: isCurrentCatHidden ? '#dc2626' : '#16a34a',
                            border: `1px solid ${isCurrentCatHidden ? '#fecaca' : '#bbf7d0'}`,
                            borderRadius: '20px',
                            padding: '4px 12px',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '5px',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                        }}
                        title={isCurrentCatHidden ? "Click to make this category visible to public customers" : "Click to hide this category from public customers"}
                    >
                        {isCurrentCatHidden ? '🙈 Category Hidden (Click to Unhide)' : '👁️ Category Visible (Click to Hide)'}
                    </button>
                </div>
            )}

            {(() => {
                const headingVal = currentCategoryObj 
                    ? stripHtml(currentCategoryObj.name || '') 
                    : (isAllProductsView 
                        ? ((settings.shopHeroHeading !== undefined && settings.shopHeroHeading !== null) 
                            ? stripHtml(settings.shopHeroHeading) 
                            : stripHtml(ECOMMERCE_BRANDING.shopHeroHeading || '')) 
                        : stripHtml(activeCategory || ''));
                const rawSubheading = currentCategoryObj 
                    ? (currentCategoryObj.description || '') 
                    : (isAllProductsView 
                        ? ((settings.shopHeroSubheading !== undefined && settings.shopHeroSubheading !== null) 
                            ? settings.shopHeroSubheading 
                            : (ECOMMERCE_BRANDING.shopHeroSubheading || '')) 
                        : '');
                const isSubheadingDuplicate = rawSubheading && headingVal && stripHtml(rawSubheading).trim().toLowerCase() === headingVal.trim().toLowerCase();
                const subheadingVal = isSubheadingDuplicate && !isEditing ? '' : rawSubheading;

                const headingFontWeight = (settings.shopHeroHeadingWeight !== undefined && settings.shopHeroHeadingWeight !== '') 
                    ? settings.shopHeroHeadingWeight 
                    : ((settings.shopHeroHeadingFontWeight !== undefined && settings.shopHeroHeadingFontWeight !== '')
                        ? settings.shopHeroHeadingFontWeight
                        : (heroConfig.titleFontWeight || heroConfig.titleWeight || styleSettings?.shopHeroHeadingWeight || '800'));

                const subheadingFontWeight = (settings.shopHeroSubheadingWeight !== undefined && settings.shopHeroSubheadingWeight !== '') 
                    ? settings.shopHeroSubheadingWeight 
                    : ((settings.shopHeroSubheadingFontWeight !== undefined && settings.shopHeroSubheadingFontWeight !== '')
                        ? settings.shopHeroSubheadingFontWeight
                        : (heroConfig.subtitleFontWeight || heroConfig.subtitleWeight || styleSettings?.shopHeroSubheadingWeight || '400'));

                const headingSize = settings.shopHeroHeadingSize || heroConfig.titleSize || heroConfig.titleFontSize;
                const subheadingSize = settings.shopHeroSubheadingSize || heroConfig.subtitleSize || heroConfig.subtitleFontSize;

                const effectiveHeroTitle = {
                    title: headingVal,
                    titleFontWeight: headingFontWeight,
                    titleWeight: headingFontWeight,
                    fontWeight: headingFontWeight,
                    weight: headingFontWeight,
                    titleSize: headingSize,
                    titleColor: settings.shopHeroHeadingColor || heroConfig.titleColor || '#0f172a',
                    titleFontFamily: settings.shopHeroHeadingFontFamily || heroConfig.titleFontFamily,
                    titleAlign: settings.shopHeroHeadingAlign || heroConfig.titleAlign || 'center',
                    titlePreset: settings.shopHeroHeadingPreset || heroConfig.titlePreset,
                    titleTextShadow: heroConfig.titleTextShadow,
                    titleTextShadowPreset: heroConfig.titleTextShadowPreset || 'none',
                    titleTextShadowCustom: heroConfig.titleTextShadowCustom || '',
                    textShadow: heroConfig.titleTextShadow,
                    textShadowPreset: heroConfig.titleTextShadowPreset || 'none',
                    textShadowCustom: heroConfig.titleTextShadowCustom || '',
                    titleTextGradientMode: heroConfig.titleTextGradientMode,
                    titleTextGradientStart: heroConfig.titleTextGradientStart,
                    titleTextGradientEnd: heroConfig.titleTextGradientEnd,
                    titleTextGradientAngle: heroConfig.titleTextGradientAngle
                };

                const effectiveHeroSubtitle = {
                    subtitle: subheadingVal,
                    subtitleFontWeight: subheadingFontWeight,
                    subtitleWeight: subheadingFontWeight,
                    subFontWeight: subheadingFontWeight,
                    subWeight: subheadingFontWeight,
                    subtitleSize: subheadingSize,
                    subtitleColor: settings.shopHeroSubheadingColor || heroConfig.subtitleColor || '#64748b',
                    subtitleFontFamily: settings.shopHeroSubheadingFontFamily || heroConfig.subtitleFontFamily,
                    subtitleAlign: settings.shopHeroSubheadingAlign || heroConfig.subtitleAlign || 'center',
                    subtitlePreset: settings.shopHeroSubheadingPreset || heroConfig.subtitlePreset,
                    subtitleTextShadow: heroConfig.subtitleTextShadow,
                    subtitleTextShadowPreset: heroConfig.subtitleTextShadowPreset || 'none',
                    subtitleTextShadowCustom: heroConfig.subtitleTextShadowCustom || '',
                    textShadow: heroConfig.subtitleTextShadow,
                    textShadowPreset: heroConfig.subtitleTextShadowPreset || 'none',
                    textShadowCustom: heroConfig.subtitleTextShadowCustom || '',
                    subtitleTextGradientMode: heroConfig.subtitleTextGradientMode,
                    subtitleTextGradientStart: heroConfig.subtitleTextGradientStart,
                    subtitleTextGradientEnd: heroConfig.subtitleTextGradientEnd,
                    subtitleTextGradientAngle: heroConfig.subtitleTextGradientAngle
                };

                return (
                <>
                    {(isEditing || headingVal) && (
                        <Editable 
                            as="h1" 
                            className="shop-hero-h1"
                            isEditing={isEditing} 
                            value={headingVal} 
                            onChange={e => {
                                const newName = e.target.value;
                                if (currentCategoryObj || !isAllProductsView) {
                                    if (handleEditCategory) {
                                        handleEditCategory(currentCategoryObj || activeCategory, { name: newName });
                                    } else if (currentCategoryObj) {
                                        const oldName = currentCategoryObj.name;
                                        const updatedCats = dbCategories.map(c => c.id === currentCategoryObj.id ? { ...c, name: newName } : c);
                                        if (setDbCategories) setDbCategories(updatedCats);
                                        if (setProducts) {
                                            setProducts(prev => prev.map(p => {
                                                let updated = { ...p };
                                                if (currentCategoryObj.parentId) {
                                                    if (p.subCategory === oldName) updated.subCategory = newName;
                                                } else {
                                                    if (p.category === oldName) updated.category = newName;
                                                }
                                                return updated;
                                            }));
                                        }
                                    }
                                } else if (update) {
                                    update(['settings', 'shopHeroHeading'], newName);
                                    update(['shopHero', 'title'], newName);
                                }
                            }} 
                            onStyleChange={(p, v) => {
                                if (!update) return;
                                const keys = normalizeHeroTitleProp(p);
                                keys.forEach(k => update(['shopHero', k], v));
                                if (p === 'Weight' || p === 'FontWeight') {
                                    update(['settings', 'shopHeroHeadingWeight'], v);
                                    update(['settings', 'shopHeroHeadingFontWeight'], v);
                                } else if (p === 'Size' || p === 'FontSize') {
                                    update(['settings', 'shopHeroHeadingSize'], v);
                                } else {
                                    update(['settings', `shopHeroHeading${p}`], v);
                                }
                            }}
                            currentStyles={{ 
                                align: effectiveHeroTitle.titleAlign, 
                                size: effectiveHeroTitle.titleSize, 
                                color: effectiveHeroTitle.titleColor, 
                                fontFamily: effectiveHeroTitle.titleFontFamily, 
                                preset: effectiveHeroTitle.titlePreset,
                                fontWeight: headingFontWeight,
                                weight: headingFontWeight,
                                textShadow: effectiveHeroTitle.textShadow,
                                textShadowPreset: effectiveHeroTitle.textShadowPreset,
                                textShadowCustom: effectiveHeroTitle.textShadowCustom
                            }}
                            style={{ 
                                ...getHeroTitleStyles(effectiveHeroTitle, resolveFontStyles),
                                marginBottom: '10px'
                            }} 
                        />
                    )}
                    {(isEditing || subheadingVal) && (
                        <Editable 
                            as="p" 
                            type="richtext"
                            className="shop-hero-subheading"
                            isEditing={isEditing} 
                            value={subheadingVal} 
                            onChange={e => {
                                const newDesc = e.target.value;
                                if (currentCategoryObj || !isAllProductsView) {
                                    if (handleEditCategory) {
                                        handleEditCategory(currentCategoryObj || activeCategory, { description: newDesc });
                                    } else if (currentCategoryObj) {
                                        const updatedCats = dbCategories.map(c => c.id === currentCategoryObj.id ? { ...c, description: newDesc } : c);
                                        if (setDbCategories) setDbCategories(updatedCats);
                                    }
                                } else if (update) {
                                    update(['settings', 'shopHeroSubheading'], newDesc);
                                    update(['shopHero', 'subtitle'], newDesc);
                                }
                            }} 
                            onStyleChange={(p, v) => {
                                if (!update) return;
                                const keys = normalizeHeroSubtitleProp(p);
                                keys.forEach(k => update(['shopHero', k], v));
                                if (p === 'Weight' || p === 'FontWeight') {
                                    update(['settings', 'shopHeroSubheadingWeight'], v);
                                    update(['settings', 'shopHeroSubheadingFontWeight'], v);
                                } else if (p === 'Size' || p === 'FontSize') {
                                    update(['settings', 'shopHeroSubheadingSize'], v);
                                } else {
                                    update(['settings', `shopHeroSubheading${p}`], v);
                                }
                            }}
                            currentStyles={{ 
                                align: effectiveHeroSubtitle.subtitleAlign, 
                                size: effectiveHeroSubtitle.subtitleSize, 
                                color: effectiveHeroSubtitle.subtitleColor, 
                                fontFamily: effectiveHeroSubtitle.subtitleFontFamily, 
                                preset: effectiveHeroSubtitle.subtitlePreset,
                                fontWeight: subheadingFontWeight,
                                weight: subheadingFontWeight,
                                textShadow: effectiveHeroSubtitle.textShadow,
                                textShadowPreset: effectiveHeroSubtitle.textShadowPreset,
                                textShadowCustom: effectiveHeroSubtitle.textShadowCustom
                            }}
                            style={getHeroSubtitleStyles(effectiveHeroSubtitle, resolveFontStyles)} 
                        />
                    )}
                </>
            );})()}
        </div>
    );
};

export default ShopHero;
